import type { THosStatus } from "hos-tools";
import DutyStatusLog from "../model/DutyStatusLog";
import idSerializer from "geotab-uuid";

export default function logDurationCalculator () {
    let logsToAdd: DutyStatusLog[] = [],
        logstoEdit: DutyStatusLog[] = [],
        hasSameDateTime = (log1: DutyStatusLog, log2: DutyStatusLog) => log1.getDate().getTime() === log2.getDate().getTime(),
        hasSameStatus = (log1: DutyStatusLog, log2: DutyStatusLog) => log1.status === log2.status,
        getBeforeAfterLogs: (logCollection: DutyStatusLog[], searchDate: Date) => ([DutyStatusLog | null, DutyStatusLog | null]) = (logCollection: DutyStatusLog[], searchDate: Date) => {
            let beforeLog: DutyStatusLog | null = null;
            let nextLog: DutyStatusLog | null = null;

            for (let i = 0; i < logCollection.length; i++) {
                let log = logCollection[i];
                if (log.getDate() > searchDate) {
                    nextLog = log;
                    break;
                }
                beforeLog = logCollection[i];
            }

            return [beforeLog, nextLog];
        },
        getLogsToUpdateForDuration = (logCollection: DutyStatusLog[], newLog: DutyStatusLog, toDate: Date) => {
            let logsToUpdate: DutyStatusLog[] = [];
            let fromDate = newLog.getDate();
            for (let i = 0; i < logCollection.length; i++) {
                let log = logCollection[i];
                let logDate = log.getDate();
                if (logDate > fromDate) {
                    if (log.id === newLog.id) {
                        continue;
                    }
                    logsToUpdate.push(log);
                    if (logDate > toDate) {
                        break;
                    }
                }
            }
            return logsToUpdate;
        },
        addNewLog = (dateTime: string, status: THosStatus, newLog: DutyStatusLog) => {
            return new DutyStatusLog({
                id: idSerializer.get(),
                status: status,
                dateTime: dateTime,
                origin: "Manual",
                device: newLog.device,
                driver: newLog.driver
            });
        },
        handleStartOfRange = (newLog: DutyStatusLog, logEndDate: Date, beforeLog?: DutyStatusLog | null, afterLog?: DutyStatusLog | null) => {
            let logClone: DutyStatusLog | null = null;
            if (beforeLog && hasSameDateTime(newLog, beforeLog)) {
                logClone = beforeLog.clone();
                logClone.status = newLog.status;
                logstoEdit.push(logClone);
            } else {
                if (afterLog && hasSameStatus(newLog, afterLog) && logEndDate > afterLog.getDate()) {
                    logClone = afterLog.clone();
                    logClone.dateTime = newLog.dateTime;
                    logstoEdit.push(logClone);
                } else {
                    logsToAdd.push(newLog);
                }
            }
            return logClone;
        },
        editInBetweenLogs = (newStatus: THosStatus, inDurationLogs: DutyStatusLog[], endRangeDate: Date, endLog: DutyStatusLog) => {
            let inBetweenLogs = inDurationLogs.filter(log => log.getDate() < endRangeDate);
            inBetweenLogs.forEach(log => {
                if (log.status !== newStatus && log.id !== endLog.id) {
                    let logClone = log.clone();
                    logClone.status = newStatus;
                    logstoEdit.push(logClone);
                }
            });
        },
        handleEndOfRange = (newLog: DutyStatusLog, inDurationLogs: DutyStatusLog[], logEndDate: Date) => {
            let oppositeStatus: THosStatus = newLog.status === "Work" ? "Rest" : "Work";
            if (inDurationLogs.length) {
                let [beforeLog, afterLog] = getBeforeAfterLogs(inDurationLogs, logEndDate);
                let logToUpdate = beforeLog || afterLog;

                if (logToUpdate) {
                    if (logToUpdate.getDate().getTime() !== logEndDate.getTime() || logToUpdate.status !== oppositeStatus) {
                        let logClone = logToUpdate.clone();
                        logClone.status = oppositeStatus;
                        logClone.dateTime = logEndDate.toISOString();
                        logstoEdit.push(logClone);
                    }
                    editInBetweenLogs(newLog.status, inDurationLogs, logEndDate, logToUpdate);
                }
            } else {
                // No logs after new log, but duration shorter than current date. Add end log of opposite status
                logsToAdd.push(addNewLog(logEndDate.toISOString(), oppositeStatus, newLog));
            }
        },
        calculateNewAndUpdatedLogs = (logCollection: DutyStatusLog[], newLog: DutyStatusLog, logEndDate: Date, isEdit: boolean): [DutyStatusLog[], DutyStatusLog[]] => {
            logsToAdd = [];
            logstoEdit = [];

            let editedBeforeLog: DutyStatusLog | null = null;
            if (!isEdit) {
                let [beforeLog, afterLog] = getBeforeAfterLogs(logCollection, newLog.getDate());
                editedBeforeLog = handleStartOfRange(newLog, logEndDate, beforeLog, afterLog);
            }

            let inDurationLogs = logEndDate ? getLogsToUpdateForDuration(logCollection, newLog, logEndDate) : [];
            if (editedBeforeLog) {
                inDurationLogs = inDurationLogs.filter(log => log.id !== (editedBeforeLog as DutyStatusLog).id);
            }

            handleEndOfRange(newLog, inDurationLogs, logEndDate);

            return [logsToAdd, logstoEdit];
        };

    return {
        getLogsToAddEdit: (logCollection: DutyStatusLog[], newLog: DutyStatusLog, logEndDate: Date, isEdit: boolean): [DutyStatusLog[], DutyStatusLog[]] => {
            return calculateNewAndUpdatedLogs(logCollection, newLog, logEndDate, isEdit);
        }
    };
}